import React, { useEffect, useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, DocumentIcon } from '@heroicons/react/24/outline';
import onboardingService from '../../services/onboardingService';
import { errText } from '../../services/scheduleService';
import { ageFrom, fmtDob } from '../../utils/saId';

const DOC_LABEL = { id_document: 'ID document', medical_aid_card: 'Medical aid card' };
const REL = { main_member: 'Main member', spouse: 'Spouse', child: 'Child', other: 'Dependant' };
const SEX = { M: 'Male', F: 'Female' };

/**
 * OnboardingReview — an administrator checks what a patient sent before it is used.
 *   New patient     the record already exists flagged "Awaiting review"; fix anything
 *                   on it (Open patient record), then approve.
 *   Already on file nothing has changed yet; the differences are listed and are
 *                   applied only when approved.
 */
const OnboardingReview = ({ onboardingId, onClose, onDecided, onOpenPatient }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [urls, setUrls] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let alive = true;
    const made = [];
    onboardingService.detail(onboardingId)
      .then(async (detail) => {
        if (!alive) return;
        setD(detail);
        for (const doc of detail.documents) {
          try {
            const url = await onboardingService.documentUrl(doc.document_id);
            made.push(url);
            if (alive) setUrls((u) => ({ ...u, [doc.document_id]: url }));
          } catch { /* shown as unavailable */ }
        }
      })
      .catch((e) => setError(errText(e)));
    return () => {
      alive = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [onboardingId]);

  const decide = async (kind) => {
    if (kind === 'reject' && !note.trim()) {
      setError('Add a note saying why it’s being rejected.');
      return;
    }
    setBusy(kind);
    setError('');
    try {
      if (kind === 'approve') await onboardingService.approve(onboardingId, note);
      else await onboardingService.reject(onboardingId, note);
      onDecided();
    } catch (e) {
      setError(errText(e));
      setBusy('');
    }
  };

  const inv = d?.invite;
  const sub = inv?.submission;
  const p = sub?.patient;
  const cover = sub?.cover;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-deep/50 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="my-6 w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-brand-deep">Review onboarding{p ? ` — ${p.first_names} ${p.surname}` : ''}</h2>
            {inv && (
              <p className="text-xs text-slate-400">
                Sent {new Date(inv.submitted_at).toLocaleString('en-ZA')} · consent accepted {new Date(inv.consented_at).toLocaleString('en-ZA')}{inv.submitted_ip ? ` from ${inv.submitted_ip}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {!d ? (
          <p className="px-6 py-10 text-center text-slate-400">{error || 'Loading…'}</p>
        ) : (
          <div className="flex flex-col gap-4 px-6 py-5 text-sm">
            {inv.matched_existing ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-blue-900">
                <p className="flex items-center gap-2 font-semibold"><InformationCircleIcon className="h-5 w-5" /> Already a patient: {d.patient?.first_names} {d.patient?.surname}</p>
                <p className="mt-0.5 text-xs">Nothing on their record has changed. {d.changes.length ? 'Approving applies the changes below.' : 'What they sent matches the record.'}</p>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
                <span>A new patient record was created and is flagged <strong>Awaiting review</strong>. Correct anything on the record first, then approve.</span>
                <button onClick={() => onOpenPatient(inv.patient_id)} className="rounded-md bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100">Open patient record</button>
              </div>
            )}

            {sub?.cover_note && (
              <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-red-700"><ExclamationTriangleIcon className="h-5 w-5 shrink-0" />{sub.cover_note}</p>
            )}
            {d.duplicates.length > 0 && (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
                <p className="font-semibold">Possible duplicate — same surname and date of birth:</p>
                {d.duplicates.map((x) => (
                  <button key={x.patient_id} onClick={() => onOpenPatient(x.patient_id)} className="mr-3 underline">
                    {[x.title, x.first_names, x.surname].filter(Boolean).join(' ')}{x.id_number ? ` (${x.id_number})` : ''}
                  </button>
                ))}
              </div>
            )}

            {inv.matched_existing && d.changes.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-semibold">Field</th>
                      <th className="px-3 py-2 font-semibold">On file</th>
                      <th className="px-3 py-2 font-semibold">Patient sent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.changes.map((c) => (
                      <tr key={c.field}>
                        <td className="px-3 py-2 font-semibold text-slate-600">{c.label}</td>
                        <td className="px-3 py-2 text-slate-500">{c.current ?? '—'}</td>
                        <td className="px-3 py-2 font-semibold text-slate-800">{c.submitted ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {p && (
              <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 rounded-lg border border-slate-200 p-3 sm:grid-cols-2">
                <Row label="Name">{[p.title, p.first_names, p.surname].filter(Boolean).join(' ')}</Row>
                <Row label={p.id_type === 'passport' ? 'Passport' : 'ID number'}>{p.id_number || 'None'}</Row>
                <Row label="Born">{fmtDob(p.date_of_birth)} ({ageFrom(p.date_of_birth)} y) · {SEX[p.sex]}</Row>
                <Row label="Contact">{[p.cell_phone, p.email].filter(Boolean).join(' · ') || '—'}</Row>
                <Row label="Address">{[p.street_address, p.suburb, p.city, p.postal_code].filter(Boolean).join(', ') || '—'}</Row>
                <Row label="Billing">
                  {p.payer === 'private'
                    ? 'Pays privately'
                    : cover
                      ? `${d.submission_scheme_name}${cover.plan_option ? ` ${cover.plan_option}` : ''} · ${cover.membership_number}/${cover.dependant_code} · ${REL[cover.relationship]}`
                      : 'Medical aid — no details'}
                </Row>
                {cover?.main_member && (
                  <Row label="Main member">{[cover.main_member.title, cover.main_member.initials, cover.main_member.surname].filter(Boolean).join(' ')}{cover.main_member.id_number ? ` (${cover.main_member.id_number})` : ''}</Row>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {d.documents.map((doc) => (
                <div key={doc.document_id} className="rounded-lg border border-slate-200 p-2">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{DOC_LABEL[doc.kind]}</p>
                  {!urls[doc.document_id] ? (
                    <p className="py-6 text-center text-xs text-slate-400">Loading…</p>
                  ) : doc.mime_type === 'application/pdf' ? (
                    <a href={urls[doc.document_id]} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-4 font-semibold text-brand-mid hover:underline">
                      <DocumentIcon className="h-5 w-5" /> Open PDF
                    </a>
                  ) : (
                    <a href={urls[doc.document_id]} target="_blank" rel="noreferrer" title="Open full size">
                      <img src={urls[doc.document_id]} alt={DOC_LABEL[doc.kind]} className="max-h-64 w-full rounded object-contain" />
                    </a>
                  )}
                </div>
              ))}
              {d.documents.length === 0 && <p className="text-slate-400">No documents were uploaded.</p>}
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-[0.8rem] font-semibold text-slate-600">Review note (required to reject)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[60px] rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40" />
            </label>

            {error && <p className="text-red-600">{error}</p>}

            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <button onClick={() => decide('reject')} disabled={!!busy} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                {busy === 'reject' ? 'Rejecting…' : inv.matched_existing ? 'Reject changes' : 'Reject'}
              </button>
              <button onClick={() => decide('approve')} disabled={!!busy} className="rounded-lg bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
                {busy === 'approve' ? 'Approving…' : inv.matched_existing && d.changes.length ? 'Approve & apply changes' : 'Approve'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex gap-2">
    <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-slate-700">{children}</span>
  </div>
);

export default OnboardingReview;
